import { Component, EventEmitter, input, Output, ViewChild, inject, OnChanges, computed } from '@angular/core';
import { MatPaginator, PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faPlusSquare } from '@fortawesome/pro-solid-svg-icons';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { ErrorLink } from '@r1-compass-library/models/reporting/reporting-data.model';
import { ReportDataElement } from '@r1-compass-library/constants/reporting.constants';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ErrorTableComponent, ErrorTableData } from '../error-table/error-table.component';
import { CommonModule } from '@angular/common';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
    DatabaseErrorTableRow,
    InteractionErrorTableComponent,
    ProcessingErrorTableRow,
    SalesforceErrorTableRow,
} from '../interaction-error-table/interaction-error-table.component';
import { PlayExecutionInteractionErrorTableData } from '@r1-compass-library/services/reporting/play-execution-interaction-error-table.service';

@Component({
    selector: 'r1-compass-lib-report-table',
    imports: [
        InteractionErrorTableComponent,
        MatPaginatorModule,
        MatSortModule,
        MatTableModule,
        MatButtonToggleModule,
        NgxChartsModule,
        ErrorTableComponent,
        CommonModule,
    ],
    templateUrl: './report-table.component.html',
    styleUrl: './report-table.component.scss',
    animations: [
        trigger('detailExpand', [
            state('collapsed, void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class ReportTableComponent<IData, OData extends Pick<any, 'executionId'>> implements OnChanges {
    tableData!: MatTableDataSource<IData>;
    pageSize = 25;
    expandedElement: unknown;
    showErrorTable = false;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    readonly dateFormat = input<string>('yyyy-MM-dd HH:mm:ssZZZ');

    readonly reportData = input<IData[] | null>([]);

    readonly appSpecificColumns = input<ReportDataElement[]>([]);

    readonly appSpecificDetails = input<ReportDataElement[]>([]);

    readonly appSpecificErrorLinks = input<ErrorLink[]>([]);

    errorTableData = input.required<ErrorTableData[]>();

    readonly databaseErrorData = input<DatabaseErrorTableRow[] | null>(null);

    readonly processingErrorData = input<ProcessingErrorTableRow[] | null>(null);

    readonly salesforceErrorData = input<SalesforceErrorTableRow[] | null>(null);

    readonly interactionErrorTableData = input<PlayExecutionInteractionErrorTableData | null>(null);

    @Output()
    pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

    @Output()
    expandRow: EventEmitter<OData> = new EventEmitter<OData>();

    @Output()
    showAppSpecificErrorDetails: EventEmitter<PlayErrorCountSearch> = new EventEmitter<PlayErrorCountSearch>();

    @Output()
    downloadSupportDetails: EventEmitter<OData> = new EventEmitter<OData>();

    protected errorColumnsToDisplay: { property: string; label: string }[] = [
        { property: 'startDate', label: 'Start Date' },
        { property: 'endDate', label: 'End Date' },
        { property: 'elapsedSeconds', label: 'Elapsed Seconds' },
        { property: 'error', label: 'Error' },
        { property: 'errorType', label: 'Error Type' },
    ] as const satisfies { property: string; label: string }[];

    columnsToDisplay = computed(() => {
        const reportData = this.reportData();
        if (reportData && reportData.length > 0) {
            return ['startDate', 'endDate', 'status', ...this.appSpecificColumns().map(column => column.property)];
        } else {
            return [];
        }
    });

    isLoading = computed(() => !this.reportData());

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faPlusSquare);
    }

    ngOnChanges() {
        const reportData = this.reportData();
        if (reportData && reportData.length > 0) {
            this.tableData = new MatTableDataSource<IData>(reportData);
        } else {
            this.tableData = new MatTableDataSource<IData>([]);
        }
        this.tableData.paginator = this.paginator;
        this.tableData.sort = this.sort;
    }

    onPageChange(pageEvent: PageEvent) {
        this.pageChange.emit(pageEvent);
    }

    onShowAppSpecificErrorDetails(errorType: string, element: OData) {
        const playErrorCountSearch: PlayErrorCountSearch = {
            executionId: element.executionId,
            errorType: errorType,
        };
        this.showAppSpecificErrorDetails.emit(playErrorCountSearch);
    }

    onExpandRow(element: OData) {
        // logic to expand/collapse a row and only emit event on expand
        if (this.expandedElement === element) {
            this.expandedElement = null;
        } else {
            this.showErrorTable = false;
            this.expandedElement = element;
            this.expandRow.emit(element);
        }
    }

    expandErrorDetails() {
        this.showErrorTable = !this.showErrorTable;
    }

    onDownloadSupportDetails(element: OData) {
        this.downloadSupportDetails.emit(element);
    }
}

export type PlayErrorCountSearch = {
    executionId: string;
    errorType: string;
};
